// ABOUTME: Reusable positivity ratio donut chart component for displaying behavior ratios
// ABOUTME: Extracted from TodaysPositivityCard logic to be used in DailyBehaviorsCard (Issue #962)

import React from 'react';
import {
  Box,
  Typography
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import {
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  Tooltip
} from 'recharts';

interface BehaviorCounts {
  stars: number;
  triangles: number;
  squares: number;
}

interface PositivityDonutChartProps {
  behaviorCounts: BehaviorCounts;
  size?: number; // Chart size in pixels, defaults to 200
}

interface ChartData {
  name: string;
  value: number;
  color: string;
  actualCount: number;
  [key: string]: string | number; // Index signature for Recharts compatibility
}

const PositivityDonutChart: React.FC<PositivityDonutChartProps> = ({
  behaviorCounts,
  size = 200
}) => {
  const theme = useTheme();
  const { stars, triangles, squares } = behaviorCounts;

  // Calculate positive ratio (stars vs triangles + squares) as #:1 format
  const totalNegative = triangles + squares;
  const positiveRatio = totalNegative === 0
    ? (stars > 0 ? '∞:1' : '0:1') // Infinite positive or no data
    : `${Math.round(stars / totalNegative)}:1`;

  // Check if no behaviors recorded
  const totalBehaviors = stars + triangles + squares;
  const isEmpty = totalBehaviors === 0;

  if (isEmpty) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        height={size}
        data-testid="empty-state-container"
      >
        <Typography color="text.secondary">
          No behaviors recorded today
        </Typography>
      </Box>
    );
  }

  // Prepare chart data (only include segments with values > 0)
  const behaviorTypes = [
    { name: 'Stars', count: stars, color: theme.palette.success.main },
    { name: 'Triangles', count: triangles, color: theme.palette.warning.main },
    { name: 'Storms', count: squares, color: theme.palette.error.main }
  ];

  const chartData: ChartData[] = behaviorTypes
    .filter(type => type.count > 0)
    .map(type => ({
      name: type.name,
      value: type.count,
      color: type.color,
      actualCount: type.count
    }));

  const innerRadius = size * 0.2; // 40 pixels for 200px chart
  const outerRadius = size * 0.4; // 80 pixels for 200px chart

  return (
    <Box
      position="relative"
      height={size}
      width={size}
      flexShrink={0}
      data-testid="chart-size-wrapper"
    >
      <ResponsiveContainer width="100%" height="100%">
        <PieChart>
          <Pie
            data={chartData}
            cx="50%"
            cy="50%"
            innerRadius={innerRadius}
            outerRadius={outerRadius}
            paddingAngle={5}
            dataKey="value"
            startAngle={90}
            endAngle={450}
          >
            {chartData.map((entry, index) => (
              <Cell key={`cell-${index}`} fill={entry.color} />
            ))}
          </Pie>
          <Tooltip
            formatter={(value, name) => [`${value}`, name]}
            labelStyle={{ display: 'none' }}
          />
        </PieChart>
      </ResponsiveContainer>

      {/* Center Ratio Display */}
      <Box
        position="absolute"
        top="50%"
        left="50%"
        sx={{
          transform: 'translate(-50%, -50%)',
          textAlign: 'center'
        }}
      >
        <Typography variant="h4" fontWeight="bold" color="primary">
          {positiveRatio}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Positive
        </Typography>
      </Box>
    </Box>
  );
};

export default PositivityDonutChart;